import * as mapboxgl from "mapbox-gl"
import { PACoordinates } from "./coordinates"
import { PAOperation } from "./operation"
import { PAProject } from "./project"
import { PAUtil } from "./util"
import { MarkerInstance } from "./mapbox-marker"
import { PATechnicianDate } from "./technician-date";
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import { PADataControl } from "../singletons/pa-data-control";
import { PATourPlannerControl } from "../singletons/pa-tourplanner-control";
import { PASettingsControl } from "../singletons/pa-settings-control";
import { PALocation } from "./location";

export interface StoreProperties {
  id: number;
  operation_count: number;
  short_name: string;
  color: string;
  done_operations: string;
  done_operations_color: string;
  next_addable_operation_time: string;
  operation_time_tooltip: string;
  point_out: boolean
}

export interface StoreLetterProperties {
  letter: string;
}

export interface StoreGeometry {
  type: "Point";
  coordinates: [number, number];
}

export class PAStore extends FireUpdateOnChange<void> implements MarkerInstance {

  static storeMap = new Map<string, PAStore>()
  static allStores: PAStore[] = []
  static selectedStoreInMap: PAStore

  public shownMapOperations: PAOperation[] = []
  private _color?: { r: number, g: number, b: number, a: number }
  public letter: string = ''
  private _selectedStoreOperation: PAOperation
  public id: number

  public expandStoreProjects: PAProject[] = []
  public expandUnplannedProjects = true
  public expandPlannedProjects = false
  public expandDoneProjects = false

  public operationTree: OperationTree = {
    unplanned: [],
    planned: [],
    done: [],
    header: ''
  }

  constructor(
    public address_company: string,
    public address: string,
    public location: PALocation,
    public operation_ids: number[]
  ) {
    super()
    this.id = PADataControl.Instance.generateNextStoreId()
    PADataControl.Instance.storeMap.set(this.id, this)
    this.color = null
  }

  get color(): { r: number, g: number, b: number, a: number } {
    return this._color
  }

  set color(color: { r: number, g: number, b: number, a: number }) {
    this._color = color
  }

  get operations(): PAOperation[] {
    return this.operation_ids?.length ? this.operation_ids.map(id => PADataControl.Instance.getOperation(id)) : []
  }

  getCoordinates(): PACoordinates {
    return this.location.coordinates
  }

  public getColorString(change_alpha?: number): string {
    const rgba = this.getColor()
    return change_alpha ? PAUtil.rgbaToString({
      r: rgba.r,
      g: rgba.g,
      b: rgba.b,
      a: change_alpha
    }) : PAUtil.rgbaToString(rgba)
  }

  public getColor(): { r: number, g: number, b: number, a: number } {
    const project_ids = [...new Set(this.shownMapOperations.map(op => op.ticket.project.id))]
    return this.color || PAUtil.hexToRgba((project_ids.length == 1 ? PADataControl.Instance.getProject(project_ids[0]).color : '#ffffff'), .9)
  }

  get selectedStoreOperation(): PAOperation {
    return this._selectedStoreOperation
  }

  set selectedStoreOperation(operation: PAOperation) {
    this._selectedStoreOperation = operation
    this.expandSelectedStoreOperation(true)
  }

  public executeBeforeChange() {
    this.updateShownMapOperations()
  }

  toggleExpandProject(project: PAProject, status: boolean): void {
    if (status && !this.expandStoreProjects.includes(project)) {
      this.expandStoreProjects.push(project)
    } else if (!status && this.expandStoreProjects.includes(project)) {
      PAUtil.removeElementFromList(this.expandStoreProjects, project)
    }
  }

  public updateShownMapOperations(): void {
    this.shownMapOperations = this.operations
    this.updateOperationTree()
    this.updateStoreLetter()
    if (!this.shownMapOperations.includes(this.selectedStoreOperation)) {
      if (this.shownMapOperations.length) {
        this.selectedStoreOperation = this.shownMapOperations[0]
      } else {
        this.selectedStoreOperation = null
      }
    }
  }

  public updateStoreLetter(): void {
    let operations_in_planning_process = PATourPlannerControl.Instance.tourPlannerExtraOperationCollection
    const main_operation_in_planning_process = PATourPlannerControl.Instance.getOperationInPlanningProcess()
    if (main_operation_in_planning_process) operations_in_planning_process = [main_operation_in_planning_process].concat(operations_in_planning_process)

    const stores_in_planning_process = [...new Set(operations_in_planning_process.map(op => op.getStore()))]
    const store_idx = stores_in_planning_process.indexOf(this)
    this.letter = this.indexToLetter(store_idx)
  }

  private indexToLetter(idx: number): string {
    if (idx >= 0) {
      let letter = ''
      if (idx > 25) {
        const div = Math.floor(idx / 26)
        letter += this.indexToLetter(div - 1)
      }
      const mod = (idx) % 26
      return letter + String.fromCharCode(mod + 65)
    } else {
      return ''
    }
  }

  public centerInMap(map: mapboxgl.Map): void {
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([this.location.coordinates.longitude, this.location.coordinates.latitude])
    map.fitBounds(
      bounds,
      {
        padding: {top: 200, bottom: 200, left: 200, right: 200},
        speed: 1,
        maxZoom: map.getZoom(),
        minZoom: map.getZoom()
      }
    )
  }

  public updateOperationTree(): void {
    const unplanned_operations = this.shownMapOperations.filter(operation => operation.isUnassigned() && operation.isGloballyVisible())
    const planned_operations = this.operations.filter(operation => !operation.isUnassigned() && !operation.isDone())
    const done_operations = this.operations.filter(operation => operation.isDone())

    const operation_count = unplanned_operations.length + planned_operations.length + done_operations.length
    const planned_operation_count = operation_count - unplanned_operations.length

    const header = `Geplant: ${planned_operation_count}/${operation_count}`

    this.operationTree = {
      unplanned: PAStore.generateProjectSubTrees(unplanned_operations, true),
      planned: PAStore.generateProjectSubTrees(planned_operations),
      done: PAStore.generateProjectSubTrees(done_operations),
      header: header
    }
    this.expandSelectedStoreOperation()
  }

  public toMapboxStoreFeature(config?: {reference_td?: PATechnicianDate, hide_operations_time?: boolean}): GeoJSON.Feature<StoreGeometry, StoreProperties> {
    const planned_operation_count = this.shownMapOperations.filter(op => !op.isUnassigned()).length

    let operations_to_add: PAOperation[]
    if (config?.reference_td) {
      operations_to_add = this.shownMapOperations.filter(op => op.isUnassigned() && !config.reference_td.hasOperationWithIdInChangedTour(op.id) && op.checkGeneralTechnicianDoability(config.reference_td?.technician))
    } else {
      operations_to_add = this.shownMapOperations.filter(op => op.isUnassigned() && !(PATourPlannerControl.Instance.tourPlannerExtraOperationCollection.find(extra_op => extra_op.id == op.id) || op.isInPlanningProcess()))
    }

    let show_memo_symbol: boolean = false
    for (const operation of this.shownMapOperations) {
      if (operation.ticket.memos.length) show_memo_symbol = true
    }

    let operation_time_string = ''
    let tooltip = ''
    if (!config?.hide_operations_time && PASettingsControl.Instance.selectedSettingConfig.data.general.map.show_operation_times) {
      if (operations_to_add.length > 1) {
        const operation_time_list = operations_to_add.map(op => op.calculateOperationTimeMilliseconds({use_technician: config?.reference_td?.technician}) / 60000)
        const total_operation_time = operation_time_list.reduce((total_time, op_time) => total_time + op_time, 0)
        operation_time_string = `Σ${total_operation_time}min`
        const time_groups = PAUtil.groupBy(operation_time_list, t => t)
        const keys = [... new Set(operation_time_list)]
        tooltip = keys.map(key => `${time_groups[key].length}x ${key}min`).join('<br>')

      } else if (operations_to_add.length) {
        operation_time_string = `${operations_to_add[0].calculateOperationTimeMilliseconds({use_technician: config?.reference_td?.technician}) / 60000}min`
        tooltip = ''
      }
    }

    const op_in_planning_process = PATourPlannerControl.Instance.getOperationInPlanningProcess()
    return {
      type: "Feature",
      properties: {
        id: this.id,
        operation_count: this.shownMapOperations.length,
        short_name: (this.address_company ? this.address_company.slice(0, 3).toUpperCase() : '?') + (show_memo_symbol ? '!' : ''),
        color: this.getColorString(),
        done_operations: `${planned_operation_count}/${this.shownMapOperations.length}`,
        done_operations_color: planned_operation_count == this.shownMapOperations.length ? 'rgb(0,255,51)' : 'rgb(255,251,0)',
        next_addable_operation_time: operation_time_string,
        operation_time_tooltip: tooltip,
        point_out: op_in_planning_process ? !!this.shownMapOperations.find(op => op.id == op_in_planning_process.id) : false,
      },
      geometry: {
        type: "Point",
        coordinates: [this.location.coordinates.longitude, this.location.coordinates.latitude]
      }
    }
  }

  public toMapboxStoreLetterFeature(): GeoJSON.Feature<StoreGeometry, StoreLetterProperties> {
    return {
      type: "Feature",
      properties: {
        letter: this.letter || '',
      },
      geometry: {
        type: "Point",
        coordinates: [this.location.coordinates.longitude, this.location.coordinates.latitude]
      }
    }
  }

  static insertOperation(operation: PAOperation, skip_update?: boolean): PAStore {
    const store = this.getOperationsStore(operation)
    if (store) {
      if (!store.operation_ids.find(id => id == operation.id)) {
        store.operation_ids.push(operation.id)
        if (!skip_update) {
          store.fireUpdateManually()
        }
      }
    }
    return store
  }

  static generateProjectSubTrees(operations: PAOperation[], apply_global_filter?: boolean): ProjectSubtree[] {
    const subtrees: ProjectSubtree[] = []
    let allProjects = [...new Set(operations.map(operation => operation.getProject()))]
    if (apply_global_filter) allProjects = allProjects.filter(p => PAProject.selectedProjects.includes(p))
    for (const project of allProjects) {
      subtrees.push({project: project, project_operations: operations.filter(op => op.belongsToProject(project.id))})
    }
    return subtrees
  }

  private expandSelectedStoreOperation(collapse_others?: boolean): void {
    if (this.selectedStoreOperation) {
      if (PADataControl.Instance.unassignedOperationUserIds.includes(this.selectedStoreOperation.user_ids[0])) {
        this.expandUnplannedProjects = true
        if (collapse_others) {
          this.expandPlannedProjects = false
          this.expandDoneProjects = false
        }

      } else {
        if (this.selectedStoreOperation.isDone()) {
          this.expandDoneProjects = true
          if (collapse_others) {
            this.expandPlannedProjects = false
            this.expandUnplannedProjects = false
          }
        } else {
          this.expandPlannedProjects = true
          if (collapse_others) {
            this.expandUnplannedProjects = false
            this.expandDoneProjects = false
          }
        }
      }

      const project = this.selectedStoreOperation.getProject()
      if (project) {
        if (collapse_others) {
          this.expandStoreProjects = [project]
        } else {
          if (!this.expandStoreProjects.includes(project)) this.expandStoreProjects.push(project)
        }
      }
    }
  }

  static getOperationsStore(operation: PAOperation): PAStore {
    if (operation.ticket.coordinates.latitude != null && operation.ticket.coordinates.longitude != null) {
      const store_key = operation.ticket.coordinates.latitude.toString() + operation.ticket.coordinates.longitude.toString()
      if (!this.storeMap.has(store_key)) {
        const store = new PAStore(
          operation.ticket.address_company,
          operation.address(),
          operation.ticket.client.location,
          [operation.id]
        )
        this.storeMap.set(store_key, store)
        store.updateShownMapOperations()
        this.allStores.push(store)
        return store
      } else {
        return this.storeMap.get(store_key)
      }
    } else {
      return null
    }
  }

  addNextDoableUnplannedOperationToTourPlanning(technician_date?: PATechnicianDate) {
    const unassigned_operations = this.getUnassignedOperations()

    if (technician_date) {
      const doable_operations = this.getDoableOperationsForTechnicianDate(technician_date)
      if (doable_operations.length) {
        PATourPlannerControl.Instance.addOperationsToTourPlanning(doable_operations);
        PATourPlannerControl.Instance.planningInstructions.addOperationsTechnicianConstraints([doable_operations[0]], [technician_date.technician], [technician_date.day.utc_timestamp])
      }
    } else {
      if (unassigned_operations.length) {
        if (PATourPlannerControl.Instance.planningMenuIsInPlanningProcess()) {
          PATourPlannerControl.Instance.addOperationsToTourPlanning(unassigned_operations);
        } else {
          PATourPlannerControl.Instance.planOperation(unassigned_operations.splice(0, 1)[0])
          PATourPlannerControl.Instance.addOperationsToTourPlanning(unassigned_operations);
        }
      }
    }
  }

  getUnassignedOperations(): PAOperation[] {
    return this.shownMapOperations.filter(op => op.isUnassigned())
  }

  getDoableOperationsForTechnicianDate(technician_date: PATechnicianDate): PAOperation[] {
    return this.getUnassignedOperations().filter(op => op.checkGeneralTechnicianDoability(technician_date.technician) && !technician_date.hasOperationWithIdInChangedTour(op.id))
  }
}

export interface OperationTree {
  'unplanned': ProjectSubtree[],
  'planned': ProjectSubtree[],
  'done': ProjectSubtree[],
  header: string
}

export interface ProjectSubtree {
  project: PAProject,
  project_operations: PAOperation[]
}
